import { useCallback, useContext, useEffect, useState } from 'react';
import {
  useAccountById,
  useRefreshAccountId,
  useRefreshCurrentBillingPeriod,
  useRefreshStatementDayChange,
  useRequestStatementDayChange,
  useStatementDayChange,
  useWaitForStatementDayChange,
  useGetAvailableDays,
  useProductConfig,
  useCheckStatementDayChangeEligibility,
  useRefreshStatementDayChangeEligibility
} from '@app/sdk';
import { FormProvider, useForm } from 'react-hook-form';
import { ContentHeader, FormContainer, Select, Table, SelectOption } from '@app/shared';
import { Segment, Button, Modal, ModalContent, Message, MessageContent, MessageHeader, Icon } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { ColDef } from 'ag-grid-community';
import { AccountIdContext } from '../AccountIdContext';

export default function PaymentDayChange() {
  const { accountId } = useContext(AccountIdContext);
  const refreshAccount = useRefreshAccountId(accountId);
  const [triggerWaitForStatementDayChange, setTriggerWaitForStatementDayChange] = useState(false);
  const refreshCurrentBillingPeriod = useRefreshCurrentBillingPeriod();
  const { data: account } = useAccountById(accountId);
  const { mutateAsync: requestStatementDayChange } = useRequestStatementDayChange();
  const { data: checkStatementDayChangeEligibility } = useCheckStatementDayChangeEligibility(accountId);
  const refreshEligibility = useRefreshStatementDayChangeEligibility(accountId);
  const { isLoading } = useWaitForStatementDayChange({
    accountId: accountId,
    currentStatementDay: account?.statementDay,
    enabled: triggerWaitForStatementDayChange,
  });
  const [paymentOrStatementDay, setPaymentOrStatementDay] = useState<string>('Statement Day');
  const [defaultSelectedDay, setDefaultSelectedDay] = useState(0);
  const { data: availableDays } = useGetAvailableDays({ productId: account?.productId });
  const { data: productConfig } = useProductConfig({ productId: account?.productId });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!availableDays || availableDays?.length === 0) {
      setDefaultSelectedDay(null);
      return;
    }
    setDefaultSelectedDay(availableDays[0].statementDay);
  }, [availableDays]);

  const { data: statementDayChangeHistory } = useStatementDayChange({
    accountId,
  });

  const refreshStatementDayChange = useRefreshStatementDayChange();

  const getDays = (): SelectOption[] => {
    if (availableDays) {
      const selector = paymentOrStatementDay === 'Statement Day' ? 'statementDay' : 'paymentDueDay';

      return availableDays
        .map((i) => ({
          value: i['statementDay'],
          label: i[selector],
        }))
        .sort((x, y) => x.label - y.label);
    }
    return null;
  };

  useEffect(() => {
    if (productConfig) {
      const { paymentDueDateStrategy } = productConfig['consumer-billing']['cycleParameters'];
      if (paymentDueDateStrategy) {
        setPaymentOrStatementDay(paymentDueDateStrategy === 'Fixed' ? 'Payment Day' : 'Statement Day');
      }
    }
  }, [productConfig]);

  const requestChange = useCallback(
    async (data: { newStatementDay: number }) => {
      const response = await requestStatementDayChange({ accountId, requestedStatementDay: data.newStatementDay });
      if (response.status === 'EffectiveImmediately') {
        setTriggerWaitForStatementDayChange(true);
        setTimeout(() => {
          refreshCurrentBillingPeriod();
          refreshAccount();
          refreshStatementDayChange();
        }, 1000);
      }

      if (response && response?.status !== 'Declined') {
        refreshAccount();
      }
      if (response?.status === 'Declined') {
        toast.warning(
          `Statement Day Change Request Status: ${response.status}, declined reason: ${response.declineReasons}`
        );
      }
      setTimeout(() => {
        refreshStatementDayChange();
      }, 1000);
    },
    [accountId, refreshAccount, refreshCurrentBillingPeriod, refreshStatementDayChange, requestStatementDayChange]
  );

  const formContext = useForm();

  const columnDef: ColDef[] = [
    { field: 'requestId', headerName: 'Id' },
    { field: 'status', headerName: 'Status' },
    { field: 'declineReasons', headerName: 'Decline Reasons' },
    { field: 'requestedDay', headerName: 'Requested  Statement Day' },
    { field: 'requestTimestamp', headerName: 'Time Stamp' },
  ];
  return (
    <>
      <Segment>
        <h2>{`${paymentOrStatementDay} Change`}</h2>
        <FormProvider {...formContext}>
          <FormContainer
            isSubmitting={isLoading}
            onSubmit={requestChange}
            additionalButtons={
              <Button
                secondary
                content={'Check eligibility'}
                onClick={async (e) => {
                  e.preventDefault();
                  await refreshEligibility();
                  return setModalOpen(true);
                }}
              />
            }
            noReset>
            <Select
              defaultValue={defaultSelectedDay}
              selectOptions={getDays() || []}
              name={'newStatementDay'}
              label={`New ${paymentOrStatementDay}`}
            />
          </FormContainer>
        </FormProvider>

        {statementDayChangeHistory && statementDayChangeHistory.length > 0 && (
          <Segment>
            <ContentHeader>{`${paymentOrStatementDay} Change History:`}</ContentHeader>
            <div style={{ height: '300px' }}>
              <Table items={statementDayChangeHistory} columnDefs={columnDef} noFilter />
            </div>
          </Segment>
        )}
      </Segment>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalContent>
          <Message header={'Eligibility'}>
            <MessageHeader>
              <Icon name="info circle" />
              Statement Day Change Eligibility
            </MessageHeader>
            <MessageContent>
              <b>Eligible:</b> {checkStatementDayChangeEligibility?.isEligible ? 'Yes' : 'No'}
              <br />
              {checkStatementDayChangeEligibility?.isEligible === false &&
                ' Reason: ' + checkStatementDayChangeEligibility?.reason}
            </MessageContent>
          </Message>
        </ModalContent>
      </Modal>
    </>
  );
}
